import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListOrganizations, adminCreateOrganization, adminUpdateOrganization, adminDeleteOrganization } from "@/lib/apiClient";
import { Plus, Save, Trash2, X, Building2, Users } from "lucide-react";

const ORG_TYPES = ["school", "masjid", "madrasa", "university", "other"];
const EMPTY_CREATE = { name: "", type: "school", custom_label: "", owner_email: "" };
const EMPTY_EDIT = { name: "", type: "school", custom_label: "" };

export default function AdminOrganizations() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => setRows((await adminListOrganizations())?.organizations || []);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await adminCreateOrganization(creating);
      toast.success("Organization created");
      setCreating(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const save = async () => {
    try {
      const { org_id, name, type, custom_label } = editing;
      await adminUpdateOrganization(org_id, { name, type, custom_label });
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? This removes all its members too.`)) return;
    try {
      await adminDeleteOrganization(org.org_id);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-organizations">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937] inline-flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#064E3B]" /> Organizations
        </h1>
        <button data-testid="add-org-btn" onClick={() => setCreating({ ...EMPTY_CREATE })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
          <Plus className="w-4 h-4" /> New organization
        </button>
      </div>
      <p className="text-sm text-[#4B5563] mt-2 max-w-xl">
        Schools, masajids, madrasas, and universities running their own cohort. Set the initial owner by email (they must already have an account) — the owner then invites their own teachers and students with the codes below.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((o) => (
          <div key={o.org_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4" data-testid={`org-${o.org_id}`}>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#4B5563]">
              <span>{o.type === "other" ? (o.custom_label || "Other") : o.type}</span>
              <span className="inline-flex items-center gap-1 text-[#064E3B]"><Users className="w-3 h-3" /> {o.member_count}</span>
            </div>
            <div className="font-heading text-base mt-2 text-[#1F2937]">{o.name}</div>
            <div className="text-xs text-[#4B5563] mt-1">Owner: {o.owner?.name || o.owner_id} {o.owner?.email && `· ${o.owner.email}`}</div>
            <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
              <span className="px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] text-[#4B5563]">Teacher code: <b className="text-[#1F2937]">{o.invite_code_teacher}</b></span>
              <span className="px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] text-[#4B5563]">Student code: <b className="text-[#1F2937]">{o.invite_code_student}</b></span>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing({ org_id: o.org_id, name: o.name, type: o.type, custom_label: o.custom_label || "" })}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">Edit</button>
              <button onClick={() => del(o)}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full rounded-2xl bg-white border border-[#E7E0D2] p-8 text-center text-sm text-[#4B5563]">
            No organizations yet.
          </div>
        )}
      </div>

      {creating && (
        <Modal title="New organization" onClose={() => setCreating(null)}>
          <Field label="Name" value={creating.name} onChange={(v) => setCreating({ ...creating, name: v })} />
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">Type</div>
            <select value={creating.type} onChange={(e) => setCreating({ ...creating, type: e.target.value })}
              className="w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none">
              {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          {creating.type === "other" && (
            <Field label="Custom label" value={creating.custom_label} onChange={(v) => setCreating({ ...creating, custom_label: v })} />
          )}
          <Field label="Owner email" value={creating.owner_email} onChange={(v) => setCreating({ ...creating, owner_email: v })} help="Must already have a QFC account." />
          <div className="p-0 pt-2 flex justify-end gap-2">
            <button onClick={() => setCreating(null)} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm">Cancel</button>
            <button data-testid="org-create-save" onClick={create} disabled={!creating.name.trim() || !creating.owner_email.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 text-white text-sm font-medium">
              <Save className="w-4 h-4" /> Create
            </button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit · ${editing.name}`} onClose={() => setEditing(null)}>
          <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">Type</div>
            <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
              className="w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none">
              {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          {editing.type === "other" && (
            <Field label="Custom label" value={editing.custom_label} onChange={(v) => setEditing({ ...editing, custom_label: v })} />
          )}
          <div className="p-0 pt-2 flex justify-end gap-2">
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm">Cancel</button>
            <button data-testid="org-edit-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center px-4 py-8" onClick={onClose}>
      <div className="bg-white rounded-3xl border border-[#E7E0D2] w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="org-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E7E0D2]">
          <div className="font-heading text-lg">{title}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#FDFBF7]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, help }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">{label}</div>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
      {help && <div className="text-[10px] text-[#9CA3AF] mt-1">{help}</div>}
    </label>
  );
}
